// Lazy Sentry wrapper.
//
// Importing `@sentry/react` at the top level pulls ~405 KB (@sentry/core +
// @sentry/browser) into the eager entry chunk — loaded before ANY page,
// including /login, can render. Sentry is a noop when no DSN is configured,
// so paying that cost up front (especially on the login path) is pure waste.
//
// This module keeps the SDK out of the critical path: it is loaded via a
// dynamic import() only when a DSN is present, so Rollup emits it as its own
// async chunk. `captureException` is a safe noop until the SDK has loaded.

type SentryModule = typeof import('@sentry/react');

let sentry: SentryModule | null = null;
let loading: Promise<SentryModule | null> | null = null;

/**
 * Initialize Sentry if a DSN is configured. Dynamically imports the SDK so it
 * never lands in the eager bundle. No-ops (and never loads the SDK) when the
 * DSN is empty. Safe to call once at bootstrap.
 */
export function initSentry(dsn: string | undefined, environment: string): void {
  if (!dsn || loading) return;
  loading = import('@sentry/react')
    .then((mod) => {
      mod.init({ dsn, environment, tracesSampleRate: 0 });
      sentry = mod;
      return mod;
    })
    .catch(() => null);
}

/**
 * Report an exception to Sentry. No-op until the SDK has finished loading
 * (errors thrown in the first few hundred ms before init are not reported —
 * an acceptable trade for keeping the SDK off the critical path). If the SDK
 * is still loading, the report is forwarded once it resolves.
 */
export function captureException(error: unknown, hint?: Record<string, unknown>): void {
  if (sentry) {
    sentry.captureException(error, hint);
    return;
  }
  if (loading) {
    void loading.then((mod) => mod?.captureException(error, hint));
  }
}
